/**
 * Resolve the allow-list for postMessage origins that the /preview route
 * accepts from the parent.
 *
 * Production: comma-separated `ME_WEB_DESIGNER_ORIGINS` app setting, read
 * through ~/config.js, e.g.
 *   `https://designer.viedoc.com,https://designer.staging.viedoc.com`.
 *
 * Dev fallback (no origins configured): accept `http(s)?://localhost:<any-port>`
 * and `http(s)?://designer.localhost`. This keeps local Vite + a hand-launched
 * Designer instance working without per-developer env files. IIS Express
 * defaults to HTTPS for ASP.NET projects, so both schemes must be allowed
 * in dev — production locks this down via ME_WEB_DESIGNER_ORIGINS.
 *
 * See docs/features/2-preview-route.md (postMessage protocol).
 */

import { getConfig } from "~/config";

const DEV_LOCALHOST_REGEX = /^https?:\/\/localhost(?::\d+)?$/;
const DEV_DESIGNER_LOCALHOST_REGEX = /^https?:\/\/designer\.localhost$/;

export function getAllowedOrigins() {
    // Runtime configuration first, the VITE_DESIGNER_ORIGINS build variable
    // after it - see ~/config.js.
    const raw = getConfig().designerOrigins;

    return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * @param {string} origin — value of `event.origin` (already validated by the
 *   browser to be a serialized origin string)
 * @returns {boolean}
 */
export function isAllowedOrigin(origin) {
    if (!origin || origin === "null") return false;

    const configured = getAllowedOrigins();
    if (configured.length > 0) {
        return configured.includes(origin);
    }

    // Dev / E2E-only localhost fallback. Production MUST configure the
    // designer origins; the fallback is gated so a prod deploy that forgot
    // them fails CLOSED (rejects every origin) rather than silently trusting
    // any localhost origin.
    //
    // Two gates open it:
    //   - `import.meta.env.DEV` — the Vite dev server + a hand-launched Designer.
    //   - `VITE_PREVIEW_ALLOW_LOCALHOST === "true"` — the E2E harness, which
    //     serves a *production* build via nginx on a dynamic localhost port and
    //     posts render-result same-origin, so it can't use a fixed
    //     VITE_DESIGNER_ORIGINS. Only `npm run build:e2e` sets this; real
    //     production never does, so prod stays fail-closed.
    // Both accesses are literal MemberExpression chains (see the note in
    // ~/config.js) so Vite statically substitutes them at build time.
    const allowLocalhost =
        import.meta.env.DEV ||
        import.meta.env.VITE_PREVIEW_ALLOW_LOCALHOST === "true";
    if (!allowLocalhost) return false;

    if (DEV_LOCALHOST_REGEX.test(origin)) return true;
    if (DEV_DESIGNER_LOCALHOST_REGEX.test(origin)) return true;

    return false;
}
